/**
 * @file RepPricing.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-30
 * @description "My pricing" — the reps' own pricing screen (Cam, 28 Oct 2026,
 *              P7: "A rep accessible version of pricing that is clear and
 *              simple, just showing their customers, and those sent to the rep
 *              for pricing, indicating when it was sent to them, and allowing
 *              them to download the xlsx again if they need to. When it is
 *              confirmed it should move out of their 'To Price' pile and into a
 *              'Priced' pile, from where they can pull back (so long as not yet
 *              invoiced - if it is invoiced, allow them to 'Flag for office'
 *              with a note …). Reps won't be confirming pricing they will just
 *              be choosing / inputting a price."; P9: "allow reps to see the
 *              cost of each product in a group this period, highest marked, and
 *              the customer's usual supplier and how often").
 *
 *   D10 (Cam's pick): CARDS — one card per request (customer, product, its
 *   orders) with the price box on the card; on a phone the same cards in one
 *   column. One price for a card's lines unless the rep SPLITS it first (Cam:
 *   different prices per line "Yes, but not by default, they should need to
 *   split the group first"). A price lands in the office's Priced by rep
 *   (D9); nothing on the order changes until the office confirms it. "Make
 *   this a contract price" asks the office for a contract at the price (N4).
 *   Costs and margins are the rep's own lines' (D8) — earlier sales with
 *   their cost and margin (D10 note), and the product group's costs this
 *   period, each supplier's latest bill, the highest marked (D6).
 *
 *   One delivery month at a time (Cam: "Rep view should be filtered to one
 *   month at a time, and should default to the current month … only default
 *   on first use … and just remember whatever they've last chosen"): the
 *   month is remembered per person (hooks/useScreenPreference). Invoiced
 *   lines stay.
 *
 *   Data: GET /api/rep-pricing/lines (services/repPricingService.repLines).
 *   Writes: POST /api/rep-pricing/prices | pull-back | flags |
 *   flags/:id/withdraw | contract-requests | contract-requests/withdraw — the
 *   six a REP badge may make (middleware/readOnly.js); none of these controls
 *   carries data-write-action or sits in a modal footer.
 *   Office staff (the Pricing or Costing tab) choose a rep and see that rep's
 *   screen, read only (?repId=).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import useScreenPreference from '../hooks/useScreenPreference';
import useLatestRequest from '../hooks/useLatestRequest';
import { useSlideOutButton } from './SlideOutButton';
import { useEntityModal } from './EntityModalProvider';
import Input from './Input';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import QtyWithTonnes from './QtyWithTonnes';
import SearchHelp from './SearchHelp';
import { badgeOf } from '../utils/permissions';
import { formatDateTimeLondon } from '../utils/dateFormatters';
import { ukDate } from '../utils/pricingCosting';
import { parseRepPrice, REP_PILES, REP_PILE_LABELS, NOTE_MAX } from '../utils/repPricing';
import { cleanMonth, monthLabel, shiftMonth, monthChoices } from '../utils/deliveryMonth';
import {
  unitWordsOf, priceText, marginText, liveMargin, liveMarginText, marginNegative,
  previousSaleWords, splitEntry, linesThatMay, stateWords, stateTone, latestSend,
  groupView, entryMatches, quantitiesOf, flagsOf
} from '../utils/repPricingCards';
import './RepPricing.css';

const DELIVERY_ICON = {
  ours: 'local_shipping',
  customer_collects: 'person_pin_circle',
  third_party: 'handshake',
  direct: 'factory'
};

const PILE_ICON = { to_price: 'edit_note', priced: 'outgoing_mail', invoiced: 'receipt_long', flagged: 'flag' };

const EMPTY_WORDS = {
  to_price: 'Nothing to price for this month.',
  priced: 'Nothing priced yet this month.',
  invoiced: 'Nothing invoiced yet this month.',
  flagged: 'Nothing flagged for the office this month.'
};

const okMonth = (v) => cleanMonth(v) !== null;
const okRep = (v) => Number.isInteger(Number(v)) && Number(v) > 0;

/** Words for a write's answer: what was done, and each line refused with why. */
function resultWords(results, done) {
  const list = Array.isArray(results) ? results : [];
  const ok = list.filter(r => r.outcome !== 'refused');
  const refused = list.filter(r => r.outcome === 'refused');
  const parts = [];
  if (ok.length) parts.push(done(ok));
  if (refused.length) {
    const why = [...new Set(refused.map(r => r.message).filter(Boolean))];
    parts.push(`${refused.length} not done: ${why.join(' ')}`);
  }
  return { text: parts.join(' '), tone: refused.length ? (ok.length ? 'warn' : 'error') : 'ok' };
}

function RepPricing({ user }) {
  const isRep = badgeOf(user) === 'REP';
  const { openEntity } = useEntityModal();

  const [storedMonth, setStoredMonth, monthPref] = useScreenPreference('repPricing.month', null, { accept: okMonth });
  const [storedRep, setStoredRep, repPref] = useScreenPreference('repPricing.repId', null, { accept: okRep });
  const [pile, setPile] = usePersistedState('repPricing.pile', 'to_price');
  const [search, setSearch] = usePersistedState('repPricing.search', '');
  const [splitKeys, setSplitKeys] = usePersistedState('repPricing.split', []);
  const [drafts, setDrafts] = usePersistedState('repPricing.drafts', {});
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needRep, setNeedRep] = useState(false);
  const [reps, setReps] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [flagOpen, setFlagOpen] = useState(null);       // card key
  const [flagNote, setFlagNote] = useState('');
  const [pullOpen, setPullOpen] = useState(null);       // card key (a confirmed price)
  const [askOpen, setAskOpen] = useState(null);         // card key (ask for a contract on a priced card)
  const [askNote, setAskNote] = useState('');
  const latest = useLatestRequest();
  const noticeRef = useRef(null);

  // Office staff: the reps to choose from.
  useEffect(() => {
    if (isRep) return;
    fetch(`${API_URL}/api/orders/sales-reps`, { credentials: 'include' })
      .then(r => r.json())
      .then(b => setReps((Array.isArray(b.reps) ? b.reps : []).filter(r => !r.status || r.status === 'ACTIVE')))
      .catch(() => setReps([]));
  }, [isRep]);

  const repId = !isRep && storedRep ? Number(storedRep) : null;
  const ready = monthPref.loaded && (isRep || repPref.loaded);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    setError(null);
    const q = new URLSearchParams({ limit: '200' });
    if (storedMonth) q.set('month', storedMonth);
    if (repId) q.set('repId', String(repId));
    const ticket = latest.begin();
    try {
      const res = await fetch(`${API_URL}/api/rep-pricing/lines?${q}`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!latest.isLatest(ticket)) return;
      if (!res.ok || !body.success) {
        if (body.code === 'NO_REP' && !isRep && !repId) { setNeedRep(true); setData(null); return; }
        throw new Error(body.error || 'Your pricing could not be read');
      }
      setNeedRep(false);
      setData(body);
    } catch (err) {
      if (latest.isLatest(ticket)) setError(err.message);
    } finally {
      if (latest.isLatest(ticket)) setLoading(false);
    }
  }, [ready, storedMonth, repId, isRep, latest]);

  useEffect(() => { load(); }, [load]);

  useSlideOutButton([data, pile, flagOpen, pullOpen, askOpen, busy, splitKeys]);

  const say = (n) => {
    setNotice(n);
    if (noticeRef.current) noticeRef.current.focus();
  };

  // ---------------------------------------------------------------- figures
  const month = (data && data.month) || storedMonth || null;
  const currentMonth = data ? data.current_month : null;
  const canRespond = !!(data && data.can_respond);
  const writesReady = !!(data && data.ready);
  const viewOnlyWhy = !data ? '' : !data.viewer.own
    ? `View only — this is ${data.rep.name}'s screen`
    : !canRespond ? 'Your login cannot price lines — ask an administrator'
    : !writesReady ? (data.not_ready_reason || 'Not ready yet') : '';

  const entries = useMemo(() => (data ? data.entries || [] : []), [data]);
  const shown = useMemo(() => {
    const inPile = entries.filter(e => e.pile === pile && entryMatches(e, search));
    // A split request: one card per order line, priced each on its own.
    return inPile.flatMap(e => (pile === 'to_price' && splitKeys.includes(e.key) ? splitEntry(e) : [e]));
  }, [entries, pile, search, splitKeys]);
  const sent = useMemo(() => latestSend(entries), [entries]);
  const months = useMemo(() => {
    const list = monthChoices(month || currentMonth, { before: 3, after: 2, extra: ((data && data.months) || []).map(m => m.month) });
    const byMonth = new Map(((data && data.months) || []).map(m => [m.month, m]));
    return list.map(m => {
      const n = byMonth.get(m);
      return { value: m, label: `${monthLabel(m)}${m === currentMonth ? ' (this month)' : ''}${n ? ` · ${n.lines} line${n.lines === 1 ? '' : 's'}${n.open < n.lines ? `, ${n.lines - n.open} invoiced` : ''}` : ''}` };
    });
  }, [month, currentMonth, data]);

  const draftOf = (key) => drafts[key] || { price: '', note: '', contract: false };
  const setDraft = (key, patch) => setDrafts(prev => ({ ...prev, [key]: { ...draftOf(key), ...(prev[key] || {}), ...patch } }));
  const clearDraft = (key) => setDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });

  // ---------------------------------------------------------------- writes
  const post = async (path, body) => {
    const res = await fetch(`${API_URL}/api/rep-pricing/${path}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const b = await res.json().catch(() => ({}));
    if (!res.ok || !b.success) throw new Error(b.error || 'That could not be saved');
    return b;
  };

  const savePrice = async (card) => {
    const d = draftOf(card.key);
    const price = parseRepPrice(d.price);
    const words = unitWordsOf(card);
    if (price === null) { say({ tone: 'error', text: 'Type a price above £0.' }); return; }
    const lineIds = linesThatMay(card, 'price');
    if (!lineIds.length) { say({ tone: 'error', text: card.can.price || 'These lines cannot be priced now.' }); return; }
    setBusy(card.key);
    try {
      const out = await post('prices', {
        lineIds, price, notes: d.note || '',
        ...(d.contract ? { contract_requested: true, contract_note: d.note || '' } : {})
      });
      const w = resultWords(out.results, ok => `Saved ${priceText(price, words.per)} for ${ok.length === 1 ? (card.lines.find(l => l.line_id === ok[0].line_id) || {}).order_number || 'the order' : `${ok.length} orders`} — it is in Priced, with the office to confirm.${d.contract ? (ok.some(r => r.contract_requested) ? ' You asked for it to be made a contract price.' : ` No contract asked: ${(ok.find(r => r.contract_reason) || {}).contract_reason || ''}`) : ''}`);
      say(w);
      clearDraft(card.key);
      if (card.split_of && card.lines.length === 1) {
        // The other lines of the split request stay split.
      }
      await load();
    } catch (err) {
      say({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const pullBack = async (card) => {
    const lineIds = linesThatMay(card, 'pull_back');
    if (!lineIds.length) return;
    setBusy(card.key);
    try {
      const out = await post('pull-back', { lineIds });
      say(resultWords(out.results, ok => (ok.some(r => r.mode === 'flag')
        ? 'Back in To Price. The office had confirmed the price — it stays on the order until they act; they have been told.'
        : 'Back in To Price — your price was taken back.')));
      setPullOpen(null);
      await load();
    } catch (err) {
      say({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const sendFlag = async (card) => {
    const note = flagNote.trim();
    if (!note) { say({ tone: 'error', text: 'Say what the office needs to know.' }); return; }
    const lineIds = linesThatMay(card, 'flag');
    setBusy(card.key);
    try {
      const out = await post('flags', { lineIds, note });
      say(resultWords(out.results, ok => `Flagged for the office (${ok.length} line${ok.length === 1 ? '' : 's'}).`));
      setFlagOpen(null);
      setFlagNote('');
      await load();
    } catch (err) {
      say({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const withdrawFlags = async (card) => {
    const ids = [...new Set((card.lines || []).map(l => l.flag && l.flag.id).filter(Boolean))];
    if (!ids.length) return;
    setBusy(card.key);
    try {
      for (const id of ids) await post(`flags/${id}/withdraw`, {});
      say({ tone: 'ok', text: 'Your flag was taken back.' });
      await load();
    } catch (err) {
      say({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const askContract = async (card) => {
    const lineIds = linesThatMay(card, 'contract');
    setBusy(card.key);
    try {
      const out = await post('contract-requests', { lineIds, note: askNote.trim() });
      say(resultWords(out.results, ok => `Asked the office to make it a contract price (${ok.length} line${ok.length === 1 ? '' : 's'}) — they decide.`));
      setAskOpen(null);
      setAskNote('');
      await load();
    } catch (err) {
      say({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const withdrawContract = async (card) => {
    const lineIds = linesThatMay(card, 'withdraw_contract');
    setBusy(card.key);
    try {
      const out = await post('contract-requests/withdraw', { lineIds });
      say(resultWords(out.results, () => 'Your request for a contract was taken back.'));
      await load();
    } catch (err) {
      say({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const download = async (s) => {
    if (!s || !s.send_id) return;
    try {
      const res = await fetch(`${API_URL}/api/rep-price-requests/sends/${encodeURIComponent(s.send_id)}/workbook`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'The spreadsheet could not be downloaded');
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const name = m ? decodeURIComponent(m[1]) : 'Price request.xlsx';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      say({ tone: 'error', text: err.message });
    }
  };

  const toggleSplit = (key, on) => setSplitKeys(prev => (on ? [...new Set([...prev, key])] : prev.filter(k => k !== key)));
  const toggleGroup = (key) => setOpenGroups(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const chooseMonth = (m) => { if (okMonth(m)) { setStoredMonth(m); setNotice(null); } };

  // ---------------------------------------------------------------- render
  const blockedWrite = viewOnlyWhy || null;

  const actionButton = ({ icon, label, onClick, disabled, title, invert = false, danger = false }) => (
    <button
      type="button"
      className={`btn-slideout always-out${invert ? ' btn-invert' : ''}${danger ? ' btn-danger' : ''}`}
      onClick={onClick}
      disabled={!!disabled}
      title={title || label}
    >
      <div className="icon-wrapper"><span className="material-symbols-rounded">{icon}</span></div>
      <span className="btn-label">{label}</span>
    </button>
  );

  const renderOrders = (card, words) => {
    const q = quantitiesOf(card);
    return (
      <div className="rp-orders">
        {(card.lines || []).map(l => (
          <div key={l.line_id} className="rp-order">
            <button type="button" className="rp-order-no" onClick={() => openEntity('customer_order', l.order_id)} title={`Open ${l.order_number || 'the order'}`}>
              {l.order_number || 'Order'}
            </button>
            <span className={`rp-order-date${l.delivered ? ' is-delivered' : ''}`} title={l.delivered ? 'Already delivered' : 'Delivery date'}>
              {l.delivery_date ? ukDate(l.delivery_date) : 'no date'}{l.delivered ? ' · delivered' : ''}
            </span>
            <span className="rp-order-qty">
              <QtyWithTonnes quantity={l.quantity} unitWeightKg={l.unit_kg} inline />
            </span>
            <span className="rp-order-supplied">
              {l.supplied !== null && l.supplied !== undefined
                ? <QtyWithTonnes quantity={l.supplied} unitWeightKg={l.unit_kg} label="Supplied" inline unitsOnly />
                : <span className="rp-muted" title="No supplied quantity recorded yet — the ordered quantity is shown">No supplied qty yet</span>}
            </span>
          </div>
        ))}
        {card.lines.length > 1 && (
          <div className="rp-order rp-order-total">
            <span>Total</span>
            <span className="rp-order-qty"><QtyWithTonnes quantity={q.quantity} unitWeightKg={card.unit_kg} inline unitsOnly /></span>
            <span className="rp-order-supplied">
              {q.supplied !== null
                ? <QtyWithTonnes quantity={q.supplied} unitWeightKg={card.unit_kg} label="Supplied" inline unitsOnly />
                : q.suppliedState === 'some' ? <span className="rp-muted">Supplied on some lines</span> : null}
            </span>
          </div>
        )}
        {!words.known && <div className="rp-warn"><span className="material-symbols-rounded">warning</span>{words.note}</div>}
      </div>
    );
  };

  const renderPrevious = (card, words) => {
    const list = card.previous_sales || [];
    if (!list.length) return <span className="rp-muted">None to this customer in the last 24 months</span>;
    return (
      <ul className="rp-previous">
        {list.map((p, i) => {
          const w = previousSaleWords(p, words.per);
          return (
            <li key={i}>
              <span className="rp-prev-price">{w.price}</span>
              <span className="rp-prev-when">{w.when}</span>
              {w.cost && <span className="rp-prev-cost">{w.cost}</span>}
              {w.notes.length > 0 && <span className="rp-prev-note">{w.notes.join(' · ')}</span>}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderCost = (card, words) => {
    if (!card.cost) return <span className="rp-muted">{data && data.viewer.sees_costs ? 'Not known yet' : 'Not shown'}</span>;
    const line = card.lines.length === 1 ? card.lines[0] : null;
    const from = line && line.cost ? line.cost.from : (card.lines.map(l => l.cost && l.cost.from).filter(Boolean)[0] || null);
    const haulage = line && line.cost && line.cost.haulage ? line.cost.haulage : null;
    return (
      <span>
        <b>{priceText(card.cost, words.per)}</b>
        {card.cost.value === null && <span className="rp-muted"> — differs between these orders</span>}
        {from && <span className="rp-muted"> · {from === 'confirmed' ? 'confirmed by the office' : from}</span>}
        {haulage && <span className="rp-muted"> · includes {priceText(haulage, words.per)} haulage</span>}
      </span>
    );
  };

  const renderGroup = (card, words, price) => {
    const g = groupView(card.group, words);
    if (!g) return null;
    const open = openGroups.has(card.key);
    const onHighest = g.marginOnHighest(price);
    return (
      <div className={`rp-group${open ? ' is-open' : ''}`}>
        <button type="button" className="rp-group-head" onClick={() => toggleGroup(card.key)} aria-expanded={open}>
          <span className="material-symbols-rounded">{open ? 'expand_less' : 'expand_more'}</span>
          <span className="rp-group-name">{g.name}{g.period ? ` · ${g.period}` : ''}</span>
          {g.highest && (
            <span className="rp-group-high">Highest {priceText(g.highest.perCard, words.per)}{g.highest.supplier ? ` · ${g.highest.supplier}` : ''}</span>
          )}
        </button>
        <div className="rp-group-usual">
          <span className="material-symbols-rounded">history</span>
          <span>{g.usual}{g.usualOthers ? <span className="rp-muted"> · also {g.usualOthers}</span> : null}</span>
        </div>
        {open && (
          <div className="rp-group-body">
            {!g.costsShown && <div className="rp-muted">Costs are not shown to you.</div>}
            {g.rows.length > 0 && (
            <table className="rp-group-table">
              <thead>
                <tr><th>Supplier</th><th>Product</th><th className="num">Latest bill</th></tr>
              </thead>
              <tbody>
                {g.rows.map(r => (
                  <tr key={r.key} className={`${r.highest ? 'is-highest' : ''}${r.mine ? ' is-mine' : ''}`}>
                    <td>
                      {r.supplier}
                      {r.usual && <span className="rp-tag">Usual</span>}
                    </td>
                    <td>
                      {r.product}
                      {r.mine && <span className="rp-tag rp-tag-mine">This order</span>}
                      {r.other_pack && <span className="rp-muted"> · {r.other_pack}</span>}
                    </td>
                    <td className="num">
                      {r.noBill ? <span className="rp-muted">No bill this period</span> : (
                        <>
                          <b>{priceText(r.perT, '/T')}</b>
                          {r.perUnit !== null && r.perUnit !== undefined && <span className="rp-muted"> = {priceText(r.perUnit, words.per)}</span>}
                          {r.highest && <span className="rp-tag rp-tag-high"><span className="material-symbols-rounded">arrow_upward</span>Highest</span>}
                          {(r.billed || r.range) && <span className="rp-group-sub">{[r.billed, r.range].filter(Boolean).join(' · ')}</span>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
            {g.rows.length === 0 && g.costsShown && <div className="rp-muted rp-small">No bills this period for any product in the group yet.</div>}
            {g.unbilled.length > 0 && (
              <div className="rp-muted rp-small">No bill this period: {g.unbilled.join('; ')}.</div>
            )}
            <div className="rp-muted rp-small">Each supplier's latest bill this period; bills count on the delivery date of the order they are for.</div>
          </div>
        )}
        {onHighest && (
          <div className={`rp-margin-high${marginNegative(onHighest) ? ' is-neg' : ''}`}>
            On the highest cost: {marginText(onHighest, words.per)}
          </div>
        )}
      </div>
    );
  };

  const renderPriceBox = (card, words) => {
    const d = draftOf(card.key);
    const m = liveMargin(d.price, card.cost);
    const typed = parseRepPrice(d.price);
    const contractWhy = card.can.contract_with_price;
    const priceWhy = blockedWrite || card.can.price;
    const id = `rp-price-${card.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    return (
      <div className="rp-price-area">
        <div className="rp-price-row">
          <label className="rp-price-input" htmlFor={id}>
            <span className="rp-pound">£</span>
            <input
              id={id}
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={d.price}
              disabled={!!priceWhy}
              title={priceWhy || `Your price ${words.perWords}`}
              onChange={(e) => setDraft(card.key, { price: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') savePrice(card); }}
              aria-label={`Your price ${words.perWords}`}
            />
            <span className="rp-per">{words.perWords}</span>
          </label>
          <div className="rp-live">
            {m ? <span className={marginNegative(m) ? 'is-neg' : 'is-pos'}>Margin {liveMarginText(m, words.per)}</span>
              : <span className="rp-muted">{card.cost ? 'Type a price to see the margin' : 'No cost known yet'}</span>}
            {d.price && typed === null && <span className="is-neg">Not a price</span>}
          </div>
        </div>
        {renderGroup(card, words, d.price)}
        <textarea
          className="rp-note"
          rows={2}
          maxLength={NOTE_MAX}
          placeholder="Note for the office (optional)"
          value={d.note}
          disabled={!!priceWhy}
          onChange={(e) => setDraft(card.key, { note: e.target.value })}
          aria-label="Note for the office"
        />
        <div className="rp-contract-ask" title={contractWhy || 'The office decides whether to make a contract at your price'}>
          <TickCheckbox
            id={`${id}-contract`}
            checked={!!d.contract && !contractWhy}
            disabled={!!contractWhy || !!priceWhy}
            onChange={(e) => setDraft(card.key, { contract: e.target.checked })}
            label="Make this a contract price"
          />
          {contractWhy && <span className="rp-muted rp-small">{contractWhy}</span>}
        </div>
        <div className="rp-actions">
          {card.lines.length > 1 && !card.split_of && actionButton({
            icon: 'call_split', label: 'Split — price each order', onClick: () => toggleSplit(card.key, true),
            title: 'Give each order on this card its own price'
          })}
          {card.split_of && actionButton({
            icon: 'merge', label: 'Back to one price', onClick: () => toggleSplit(card.split_of, false),
            title: 'Price the orders on this card together again'
          })}
          {actionButton({
            icon: 'send', label: busy === card.key ? 'Saving…' : 'Save price', invert: true,
            onClick: () => savePrice(card), disabled: !!priceWhy || typed === null || busy === card.key,
            title: priceWhy || (typed === null ? 'Type your price first' : `Save ${priceText(typed, words.per)} — the office confirms it`)
          })}
        </div>
      </div>
    );
  };

  const renderFlagBox = (card) => (
    <div className="rp-inline-box">
      <textarea
        className="rp-note"
        rows={3}
        maxLength={NOTE_MAX}
        placeholder="What should the office look at? (required)"
        value={flagNote}
        onChange={(e) => setFlagNote(e.target.value)}
        aria-label="Note for the office"
        autoFocus
      />
      <div className="rp-actions">
        {actionButton({ icon: 'close', label: 'Cancel', onClick: () => { setFlagOpen(null); setFlagNote(''); } })}
        {actionButton({ icon: 'flag', label: busy === card.key ? 'Sending…' : 'Send flag', danger: true,
          onClick: () => sendFlag(card), disabled: !flagNote.trim() || busy === card.key,
          title: flagNote.trim() ? 'Send this note to the office' : 'Write a note first' })}
      </div>
    </div>
  );

  const renderAfter = (card, words) => {
    const pull = linesThatMay(card, 'pull_back').length > 0;
    const pullWhy = blockedWrite || (pull ? null : (card.can.pull_back && card.can.pull_back.reason) || 'Nothing to pull back');
    const flagWhy = blockedWrite || card.can.flag;
    const asked = linesThatMay(card, 'withdraw_contract').length > 0;
    const askWhy = blockedWrite || card.can.contract;
    const confirmedLines = (card.lines || []).filter(l => l.can.pull_back && l.can.pull_back.mode === 'flag');
    const repPrice = card.rep_price;
    return (
      <div className="rp-price-area">
        <div className="rp-price-row">
          <div className="rp-price-shown">
            {repPrice ? <><span className="rp-muted">Your price</span> <b>{priceText(repPrice, words.per)}</b></> : null}
            {!repPrice && card.lines.some(l => l.office && l.office.confirmed) && (
              <><span className="rp-muted">Confirmed</span> <b>{priceText({ value: null, ...rangeOf(card.lines.map(l => l.office.confirmed && l.office.confirmed.value)) }, words.per)}</b></>
            )}
            {!repPrice && card.pile === 'invoiced' && card.lines.some(l => l.office && l.office.invoiced) && (
              <><span className="rp-muted">Invoiced</span> <b>{priceText({ value: null, ...rangeOf(card.lines.map(l => l.office.invoiced && l.office.invoiced.value)) }, words.per)}</b></>
            )}
          </div>
          {card.margin && card.margin.margin !== null && (
            <div className="rp-live"><span className={card.margin.margin < 0 ? 'is-neg' : 'is-pos'}>Margin {marginText(card.margin, words.per)}</span></div>
          )}
        </div>
        {card.rep_note && <div className="rp-muted rp-small">Your note: “{card.rep_note}”</div>}
        {renderGroup(card, words, null)}
        {card.contract_request && (
          <div className="rp-contract-said">
            <span className="material-symbols-rounded">handshake</span>
            You asked for {priceText(card.contract_request.price, words.per)} to be made a contract price{card.contract_request.at ? ` on ${ukDate(String(card.contract_request.at).slice(0, 10))}` : ''} — the office decides.
          </div>
        )}
        {pullOpen === card.key && (
          <div className="rp-inline-box">
            <p className="rp-small">
              The office has confirmed {confirmedLines.length === 1 ? priceText(confirmedLines[0].office.confirmed && confirmedLines[0].office.confirmed.value, words.per) : 'these prices'}.
              {' '}It stays on the order until they act; pulling it back tells them you want to price it again.
            </p>
            <div className="rp-actions">
              {actionButton({ icon: 'close', label: 'Cancel', onClick: () => setPullOpen(null) })}
              {actionButton({ icon: 'undo', label: busy === card.key ? 'Pulling back…' : 'Pull back', invert: true, onClick: () => pullBack(card), disabled: busy === card.key })}
            </div>
          </div>
        )}
        {flagOpen === card.key && renderFlagBox(card)}
        {askOpen === card.key && (
          <div className="rp-inline-box">
            <textarea className="rp-note" rows={2} maxLength={NOTE_MAX} placeholder="Note for the office about the contract (optional)"
              value={askNote} onChange={(e) => setAskNote(e.target.value)} aria-label="Note about the contract" autoFocus />
            <div className="rp-actions">
              {actionButton({ icon: 'close', label: 'Cancel', onClick: () => { setAskOpen(null); setAskNote(''); } })}
              {actionButton({ icon: 'handshake', label: busy === card.key ? 'Asking…' : 'Ask the office', invert: true, onClick: () => askContract(card), disabled: busy === card.key })}
            </div>
          </div>
        )}
        {pullOpen !== card.key && flagOpen !== card.key && askOpen !== card.key && (
          <div className="rp-actions">
            {actionButton({
              icon: 'undo', label: 'Back to To Price', disabled: !!pullWhy || busy === card.key,
              title: pullWhy || 'Take it back to price again',
              onClick: () => (confirmedLines.length && !card.lines.some(l => l.can.pull_back && l.can.pull_back.mode === 'withdraw')
                ? setPullOpen(card.key) : pullBack(card))
            })}
            {asked
              ? actionButton({ icon: 'remove_done', label: 'Take back contract request', disabled: !!blockedWrite || busy === card.key,
                title: blockedWrite || 'Take back your request for a contract', onClick: () => withdrawContract(card) })
              : actionButton({ icon: 'handshake', label: 'Make this a contract price', disabled: !!askWhy || busy === card.key,
                title: askWhy || 'Ask the office to make your price a contract price', onClick: () => { setAskOpen(card.key); setAskNote(''); } })}
            {actionButton({
              icon: 'flag', label: 'Flag for office', danger: true, disabled: !!flagWhy || busy === card.key,
              title: flagWhy || 'Send the office a note about this', onClick: () => { setFlagOpen(card.key); setFlagNote(''); }
            })}
          </div>
        )}
      </div>
    );
  };

  const renderFlagged = (card, words) => {
    const flags = flagsOf(card);
    const repPrice = card.rep_price;
    return (
      <div className="rp-price-area">
        <div className="rp-price-row">
          <div className="rp-price-shown">
            {repPrice ? <><span className="rp-muted">Your price</span> <b>{priceText(repPrice, words.per)}</b></> : null}
            {!repPrice && <span className="rp-muted">{stateWords(card)}</span>}
          </div>
          {card.margin && card.margin.margin !== null && (
            <div className="rp-live"><span className={card.margin.margin < 0 ? 'is-neg' : 'is-pos'}>Margin {marginText(card.margin, words.per)}</span></div>
          )}
        </div>
        {repPrice && <div className="rp-muted rp-small">{stateWords(card)}</div>}
        {flags.map(f => (
          <div key={f.ids.join('-')} className="rp-flag-said">
            <span className="material-symbols-rounded">flag</span>
            <span>
              Your note: “{f.note}”{f.at ? ` · sent ${formatDateTimeLondon(f.at)}` : ''}
              {card.lines.length > 1 && f.orders.length ? ` · on ${f.orders.join(', ')}` : ''} — the office has not dealt with it yet.
            </span>
          </div>
        ))}
        <div className="rp-actions">
          {actionButton({ icon: 'undo', label: busy === card.key ? 'Taking back…' : 'Take back my flag', disabled: !!blockedWrite || busy === card.key,
            title: blockedWrite || 'Take your flag back', onClick: () => withdrawFlags(card) })}
        </div>
      </div>
    );
  };

  const renderCard = (card) => {
    const words = unitWordsOf(card);
    const tone = stateTone(card);
    const state = card.pile === 'to_price' ? (card.split_of ? 'To price — on its own' : 'To price')
      : card.pile === 'flagged' ? 'Flagged for the office' : stateWords(card);
    const method = card.delivery_method;
    const additives = (card.additives || []).map(a => a.name || a.code).filter(Boolean);
    const resolved = (card.lines || []).flatMap(l => l.flags_recent || []).filter(f => f.resolution === 'dealt_with');
    return (
      <article key={card.key} className={`rp-card tone-${tone}${card.delivered && card.pile === 'to_price' ? ' is-delivered' : ''}`} aria-label={`${card.customer ? card.customer.name : ''} — ${card.product ? card.product.name : ''}`}>
        <header className="rp-card-head">
          <div className="rp-who">
            <div className="rp-customer">
              {card.customer && card.customer.account_no && <span className="account-badge-inline">{card.customer.account_no}</span>}
              <span>{card.customer ? card.customer.name : 'Customer'}</span>
            </div>
            <div className="rp-product">
              {card.product ? card.product.name : ''}
              {card.product && card.product.code && <span className="rp-muted"> {card.product.code}</span>}
              {additives.length > 0 && <span className="rp-additives"> + {additives.join(', ')}</span>}
            </div>
          </div>
          <span className={`rp-state tone-${tone}`} title={state}>{state}</span>
        </header>

        <div className="rp-chips">
          {method && (
            <span className="rp-chip" title="How it reaches the customer">
              <span className="material-symbols-rounded">{DELIVERY_ICON[method.kind] || 'local_shipping'}</span>{method.label}
            </span>
          )}
          <span className="rp-chip rp-chip-unit" title="What your price is per">
            <span className="material-symbols-rounded">straighten</span>Price {words.perWords}
          </span>
          {card.delivered && card.pile === 'to_price' && (
            <span className="rp-chip rp-chip-late" title="Delivered and not priced yet">
              <span className="material-symbols-rounded">event_busy</span>Delivered — needs a price
            </span>
          )}
        </div>

        <dl className="rp-facts">
          <dt>Orders</dt>
          <dd>{renderOrders(card, words)}</dd>
          <dt>{data && !data.viewer.own ? `Sent to ${data.rep.name}` : 'Sent to you'}</dt>
          <dd>
            {card.sent ? (
              <span className="rp-sent">
                <span title={card.sent.subject ? `“${card.sent.subject}”` : undefined}>
                  {formatDateTimeLondon(card.sent.at)}{card.sent.second_check ? ' · a second check' : ''}
                  {sent && card.sent.send_id !== sent.send_id && card.sent.subject ? <span className="rp-muted"> · “{card.sent.subject}”</span> : null}
                </span>
                <button
                  type="button"
                  className="rp-link"
                  onClick={() => download(card.sent)}
                  disabled={!card.sent.has_workbook || !card.sent.send_id}
                  title={card.sent.has_workbook ? 'Download the spreadsheet you were sent again' : 'No spreadsheet was kept for this send'}
                >
                  <span className="material-symbols-rounded">download</span>Spreadsheet
                </button>
              </span>
            ) : <span className="rp-muted">Not recorded</span>}
          </dd>
          <dt>Cost</dt>
          <dd>{renderCost(card, words)}</dd>
          <dt>Earlier sales</dt>
          <dd>{renderPrevious(card, words)}</dd>
        </dl>

        {resolved.length > 0 && card.pile !== 'flagged' && (
          <div className="rp-muted rp-small">
            The office dealt with your flag{resolved[0].resolved_by ? ` (${resolved[0].resolved_by})` : ''}{resolved[0].resolved_note ? `: “${resolved[0].resolved_note}”` : ''}.
          </div>
        )}

        {card.pile === 'to_price' && renderPriceBox(card, words)}
        {(card.pile === 'priced' || card.pile === 'invoiced') && renderAfter(card, words)}
        {card.pile === 'flagged' && renderFlagged(card, words)}
      </article>
    );
  };

  // ---------------------------------------------------------------- page
  const counts = (data && data.counts) || {};
  const atCurrent = month && currentMonth && month === currentMonth;

  return (
    <div className="rep-pricing-screen">
      <div className="rp-top">
        <div className="rp-title">
          <h2>My Pricing</h2>
          <span className="rp-muted">
            {data ? `${data.rep.name} · ${data.viewer.own ? 'view only, except your prices' : 'view only'}` : (isRep ? '' : 'Choose a rep to see their screen')}
          </span>
        </div>
        {!isRep && (
          <div className="rp-rep-choose">
            <Dropdown
              options={reps.map(r => ({ value: String(r.id), label: r.name }))}
              value={storedRep ? String(storedRep) : ''}
              onChange={(v) => setStoredRep(Number(v))}
              placeholder="Choose a rep…"
            />
          </div>
        )}
      </div>

      <div className="rp-month" role="group" aria-label="Delivery month">
        <button type="button" className="rp-month-step" onClick={() => chooseMonth(shiftMonth(month, -1))} disabled={!month} title="The month before" aria-label="The month before">
          <span className="material-symbols-rounded">chevron_left</span>
        </button>
        <div className="rp-month-pick">
          <Dropdown options={months} value={month || ''} onChange={chooseMonth} placeholder="Month" />
        </div>
        <button type="button" className="rp-month-step" onClick={() => chooseMonth(shiftMonth(month, 1))} disabled={!month} title="The month after" aria-label="The month after">
          <span className="material-symbols-rounded">chevron_right</span>
        </button>
        {!atCurrent && currentMonth && (
          <button type="button" className="rp-link" onClick={() => chooseMonth(currentMonth)}>This month</button>
        )}
        <span className="rp-muted rp-small">Orders delivering in {month ? monthLabel(month) : 'this month'}</span>
      </div>

      {sent && (
        <div className="rp-sent-bar">
          <span className="material-symbols-rounded">mail</span>
          <span>
            Latest request sent to {data && !data.viewer.own ? data.rep.name : 'you'} <b>{formatDateTimeLondon(sent.at)}</b>
            {sent.subject ? `: “${sent.subject}”` : ''}
          </span>
          <button type="button" className="rp-link" onClick={() => download(sent)} disabled={!sent.has_workbook || !sent.send_id}
            title={sent.has_workbook ? 'Download that spreadsheet again' : 'No spreadsheet was kept for this send'}>
            <span className="material-symbols-rounded">download</span>Download the spreadsheet again
          </button>
        </div>
      )}

      {viewOnlyWhy && data && <div className="rp-banner"><span className="material-symbols-rounded">visibility</span>{viewOnlyWhy}</div>}

      <div className="rp-controls">
        <div className="rp-piles" role="tablist" aria-label="Piles">
          {REP_PILES.map(p => {
            const n = counts[p] ? counts[p].requests : 0;
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={pile === p}
                className={`rp-pile${pile === p ? ' is-current' : ''}${p === 'to_price' && n ? ' has-work' : ''}${p === 'flagged' && n ? ' has-flags' : ''}`}
                onClick={() => setPile(p)}
              >
                <span className="material-symbols-rounded">{PILE_ICON[p]}</span>
                {p === 'flagged' ? 'Flagged' : REP_PILE_LABELS[p]}
                <span className="rp-count">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="rp-search">
          <label htmlFor="rp-search-box" className="rp-search-label">
            Search <SearchHelp fields={['customer', 'account no.', 'product', 'item code', 'additives', 'order number', 'delivery', 'product group']} />
          </label>
          <Input id="rp-search-box" type="text" icon="search" placeholder="Customer, product, order…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {notice && (
        <div ref={noticeRef} tabIndex={-1} className={`rp-notice tone-${notice.tone}`} role="status" aria-live="polite">
          <span className="material-symbols-rounded">{notice.tone === 'ok' ? 'check_circle' : notice.tone === 'warn' ? 'info' : 'error'}</span>
          <span>{notice.text}</span>
          <button type="button" className="rp-link" onClick={() => setNotice(null)} aria-label="Dismiss">Dismiss</button>
        </div>
      )}

      {error && <div className="rp-notice tone-error" role="alert"><span className="material-symbols-rounded">error</span><span>{error}</span></div>}

      {needRep && !data && (
        <div className="rp-empty">Choose a rep above to see the lines sent to them, as they see them.</div>
      )}

      {loading && !data && !needRep && <div className="rp-loading"><LoadingSpinner /></div>}

      {data && (
        <div className={`rp-cards${loading ? ' is-loading' : ''}`}>
          {shown.length === 0 ? (
            <div className="rp-empty">{search ? 'Nothing here matches your search.' : EMPTY_WORDS[pile]}</div>
          ) : shown.map(renderCard)}
        </div>
      )}
      {data && data.truncated && <div className="rp-muted rp-small">Only the most recent requests are shown.</div>}
      {data && data.total > (data.entries || []).length && (
        <div className="rp-muted rp-small">Showing {(data.entries || []).length} of {data.total} requests this month.</div>
      )}
    </div>
  );
}

/** { min, max } of the figures given (blanks left out); value when they agree. */
function rangeOf(values) {
  const list = (values || []).map(Number).filter(v => Number.isFinite(v));
  if (!list.length) return { value: null, min: null, max: null };
  const min = Math.min(...list);
  const max = Math.max(...list);
  return Math.abs(max - min) < 0.00005 ? { value: min, min, max } : { value: null, min, max };
}

export default RepPricing;
